import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip as ChartTooltip,
  Filler,
  ArcElement,
} from "chart.js";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import InventoryIcon from "@mui/icons-material/Inventory";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useHistory } from "react-router-dom";
import { getComplianceIcon, getComplianceFrameworkBadgeText } from "../../../assets/images/compliance/IconCompliance.jsx";
import { formatUtcDateSimple } from "../../../helpers/formatUtcDate";
import { useDispatch, useSelector } from "react-redux";
import {
  downloadComplianceReport,
  clearAllComplianceMessages,
} from "../../../store/compliance/complianceSlice";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  ChartTooltip,
  Filler,
  ArcElement
);

// Using exact colors from ComplianceBox
const PASSED_COLOR = "#a5d6a7"; // green for progress
const FAILED_COLOR = "#ef9a9a"; // red for background
const PASSED_TEXT_COLOR = "#43a047"; // darker green for text
const FAILED_TEXT_COLOR = "#e53935"; // darker red for text
const HEADER_BG = "#f7fafd"; // very subtle background
const TITLE_COLOR = "#1a2233";
const SUBTITLE_COLOR = "#6b7a90";
const DATE_COLOR = "#757575"; // subtle gray for date
const TREND_COLOR = "#1976d2";
const DIVIDER_COLOR = "#e3eafc";

const ControlsHeader = ({
  controls = {},
  compliance,
  scanDate,
  compliancePercent,
  runId,
  accountNumber,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Metrics
  const total = controls.totalResources || 0;
  const passed = controls.passedResources || 0;
  const failed = controls.failedResources || 0;

  // Control-level Passed/Failed (same totals as Compliance landing cards)
  const { controlPassed, controlFailed } = useMemo(() => {
    const list = controls.controlsListJson;
    if (!Array.isArray(list)) return { controlPassed: 0, controlFailed: 0 };
    return list.reduce(
      (acc, category) => ({
        controlPassed: acc.controlPassed + (category.PassedControls || 0),
        controlFailed: acc.controlFailed + (category.FailedControls || 0),
      }),
      { controlPassed: 0, controlFailed: 0 }
    );
  }, [controls.controlsListJson]);

  // Calculate percentages for progress bar
  const passedPercent = total > 0 ? (passed / total) * 100 : 0;

  // Prepare trendScore data for last 30 days
  const trendData = useMemo(() => {
    if (!controls.trendScore) return { labels: [], data: [] };
    const entries = Object.entries(controls.trendScore).sort(
      ([a], [b]) => new Date(a) - new Date(b)
    );
    // Only last 30 days
    const last30 = entries.slice(-30);
    return {
      labels: last30.map(([date]) => date.slice(5, 10)), // MM-DD for compactness
      data: last30.map(([, value]) => value),
    };
  }, [controls.trendScore]);

  let trendChange = null;
  // Use scanDate to determine 'latest' and 'prev' for delta calculation
  if (controls.trendScore && scanDate) {
    // Convert scanDate to YYYY-MM-DD
    const scanDateObj = new Date(scanDate);
    const pad = (n) => n.toString().padStart(2, "0");
    const scanDateStr = `${scanDateObj.getFullYear()}-${pad(
      scanDateObj.getMonth() + 1
    )}-${pad(scanDateObj.getDate())}`;
    // Previous day
    const prevDateObj = new Date(scanDateObj);
    prevDateObj.setDate(scanDateObj.getDate() - 1);
    const prevDateStr = `${prevDateObj.getFullYear()}-${pad(
      prevDateObj.getMonth() + 1
    )}-${pad(prevDateObj.getDate())}`;
    const latest = controls.trendScore[scanDateStr];
    const prev = controls.trendScore[prevDateStr];
    if (typeof latest === "number" && typeof prev === "number") {
      const delta = latest - prev;
      const percent = prev !== 0 ? delta : 0;
      trendChange = {
        delta,
        percent: Math.abs(percent),
        direction: delta > 0 ? "up" : delta < 0 ? "down" : "none",
      };
    }
  }

  const chartData = {
    labels: trendData.labels,
    datasets: [
      {
        label: "Trend Score",
        data: trendData.data,
        fill: true,
        borderColor: TREND_COLOR,
        backgroundColor: "rgba(25, 118, 210, 0.10)",
        pointRadius: 2,
        pointBackgroundColor: TREND_COLOR,
        tension: 0.35,
        borderWidth: 2,
      },
    ],
  };

  // External tooltip state
  const [tooltip, setTooltip] = useState({
    display: false,
    x: 0,
    y: 0,
    title: '',
    value: '',
  });
  const chartRef = useRef();

  // Custom external tooltip handler for Chart.js
  const externalTooltipHandler = useCallback((context) => {
    const { chart, tooltip } = context;
    if (!tooltip || !chart) return;
    if (tooltip.opacity === 0) {
      setTooltip((prev) => (prev.display ? { ...prev, display: false } : prev));
      return;
    }
    // Get canvas position
    const canvasRect = chart.canvas.getBoundingClientRect();
    // Tooltip position relative to page
    const tooltipWidth = 220; // Estimate, adjust as needed
    let x = canvasRect.left + window.scrollX + tooltip.caretX;
    // Smart horizontal positioning
    if (x + tooltipWidth > window.innerWidth - 12) {
      x = window.innerWidth - tooltipWidth - 12; // align right
    } else if (x < 12) {
      x = 12; // align left
    }
    // Place tooltip above or below depending on space
    let y = canvasRect.top + window.scrollY + tooltip.caretY;
    const tooltipHeight = 64; // Estimate, adjust as needed
    if (y > window.innerHeight / 2) {
      y = y - tooltipHeight - 16; // above
    } else {
      y = y + 16; // below
    }
    // Use the date as provided by the chart/labels
    let scanDateLabel = tooltip.title[0];
    // Only show the score as value
    let score = tooltip.body[0].lines[0].replace('Compliance: ', '').replace('Trend Score: ', '').trim();

    // Only update if the tooltip state actually changes
    setTooltip(prev => {
      if (
        prev.display !== true ||
        prev.x !== x ||
        prev.y !== y ||
        prev.date !== scanDateLabel ||
        prev.value !== score
      ) {
        return {
          display: true,
          x,
          y,
          title: 'Scan Date',
          date: scanDateLabel,
          value: score,
        };
      }
      return prev;
    });
  }, []);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false, // Disable default tooltip
        external: externalTooltipHandler,
      },
    },
    scales: {
      x: {
        display: true,
        grid: { color: "#f0f0f0", drawBorder: false },
        ticks: {
          color: SUBTITLE_COLOR,
          font: { size: 10, weight: 500 },
          maxTicksLimit: 6,
          padding: 4,
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        display: true,
        grid: { display: false },
        ticks: { display: false },
        min: 0,
        max: 100,
      },
    },
    elements: {
      line: { borderWidth: 2 },
      point: { radius: 2 },
    },
  }), [externalTooltipHandler]);

  const iconSrc = getComplianceIcon(compliance);
  const frameworkBadgeText = getComplianceFrameworkBadgeText(compliance);

  const dispatch = useDispatch();
  const {
    isDownloadingComplianceReport,
    downloadComplianceReportMessage,
    downloadComplianceReportError,
  } = useSelector((state) => state.compliance);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Download file when URL is received
  useEffect(() => {
    if (downloadComplianceReportMessage) {
      const link = document.createElement("a");
      link.href = downloadComplianceReportMessage;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => {
        dispatch(clearAllComplianceMessages());
      }, 500);
    }
  }, [downloadComplianceReportMessage, dispatch]);

  // Show error snackbar
  useEffect(() => {
    if (downloadComplianceReportError) {
      setSnackbar({
        open: true,
        message: downloadComplianceReportError,
        severity: "error",
      });
      setTimeout(() => {
        dispatch(clearAllComplianceMessages());
      }, 4000);
    }
  }, [downloadComplianceReportError, dispatch]);

  const handleDownloadReport = () => {
    if (compliance && runId) {
      dispatch(downloadComplianceReport({ compliance, runId: runId }));
    }
  };

  const handleBack = () => {
    history.push("/compliance");
  };

  const titleWordBreak = isMobile ? "break-word" : "normal";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 0,
        mb: 2,
        borderRadius: 3,
        background: HEADER_BG,
        minHeight: 0,
        border: "1px solid #e3eafc",
        boxShadow: "0 2px 12px 0 rgba(25, 118, 210, 0.06)",
        overflow: "hidden", // Prevent content from spilling out
      }}
    >
      <Box sx={{ p: 1.5 }}>
        {/* Responsive layout that adapts to screen size and content */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 2 : 1,
            flexWrap: isSmallScreen ? "wrap" : "nowrap",
            minHeight: isMobile ? "auto" : 80,
          }}
        >
          {/* First section: Circle, Icon, Title, Account, Scan Date (responsive) */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: isMobile ? "none" : "1 1 340px",
              minWidth: isMobile ? "100%" : 220,
              maxWidth: isMobile ? "100%" : 460,
              overflow: "hidden",
              py: 0.5,
              gap: 0.5,
            }}
          >
            {/* First Row: Circle, Icon, Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5, minWidth: 0 }}>
              {/* Compliance Percentage Circle FIRST */}
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 96,
                  height: 96,
                  flexShrink: 0,
                }}
              >
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={96}
                  thickness={3}
                  sx={{ color: FAILED_COLOR, position: "absolute" }}
                />
                <CircularProgress
                  variant="determinate"
                  value={Number(compliancePercent)}
                  size={96}
                  thickness={3}
                  sx={{
                    color:
                      Number(compliancePercent) === 0
                        ? FAILED_COLOR
                        : PASSED_COLOR,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color: TITLE_COLOR,
                      fontSize: "1rem",
                      textAlign: "center",
                      letterSpacing: 1,
                      px: 1.2,
                      background: "none",
                      borderRadius: 0,
                      minWidth: 0,
                      lineHeight: 1,
                    }}
                  >
                    {Number(compliancePercent).toFixed(2)}%
                  </Typography>
                </Box>
              </Box>
              {/* Title + Passed/Failed (fills space under framework name) */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  minWidth: 0,
                  flex: 1,
                  gap: 0.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                  {iconSrc ? (
                    <img
                      src={iconSrc}
                      alt={compliance + " icon"}
                      style={{
                        height: 24,
                        width: "auto",
                        maxWidth: 28,
                        marginRight: 8,
                        padding: 2,
                        backgroundColor: "rgba(25, 118, 210, 0.04)",
                        borderRadius: 8,
                        flexShrink: 0,
                      }}
                    />
                  ) : frameworkBadgeText ? (
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 800,
                        fontSize: "0.95rem",
                        letterSpacing: 0.4,
                        marginRight: 1,
                        padding: "2px 6px",
                        backgroundColor: "rgba(25, 118, 210, 0.04)",
                        borderRadius: 1,
                        color: TITLE_COLOR,
                        flexShrink: 0,
                        lineHeight: 1.2,
                      }}
                    >
                      {frameworkBadgeText}
                    </Typography>
                  ) : null}
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color={TITLE_COLOR}
                    sx={{
                      letterSpacing: -0.5,
                      fontSize: isMobile ? "1rem" : "1.05rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "normal",
                      minWidth: 0,
                      maxWidth: isMobile ? "100%" : 340,
                      wordBreak: "break-word",
                      lineHeight: 1.15,
                    }}
                    title={compliance}
                  >
                    {compliance}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: PASSED_TEXT_COLOR,
                      fontWeight: 700,
                      fontSize: "0.95rem",
                    }}
                  >
                    Passed: {controlPassed}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: FAILED_TEXT_COLOR,
                      fontWeight: 700,
                      fontSize: "0.95rem",
                    }}
                  >
                    Failed: {controlFailed}
                  </Typography>
                </Box>
              </Box>
            </Box>
            {/* Second Row: Account Number and Scan Date */}
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2, mt: 0.5 }}>
              {accountNumber && (
                <Typography
                  variant="caption"
                  sx={{
                    color: DATE_COLOR,
                    fontWeight: 500,
                    fontSize: "0.80rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  Account: {accountNumber}
                </Typography>
              )}
              {scanDate && (
                <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                  <CalendarMonthIcon sx={{ color: DATE_COLOR, fontSize: 14, mr: 0.5 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: DATE_COLOR,
                      fontWeight: 500,
                      fontSize: "0.80rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Scan Date: {formatUtcDateSimple(scanDate)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Divider - hidden on small screens */}
          {!isMobile && (
            <Box
              sx={{
                width: "1px",
                background: DIVIDER_COLOR,
                mx: 0.5,
                my: 1,
                alignSelf: "stretch",
                flexShrink: 0,
              }}
            />
          )}

          {/* Second section: Resources (responsive) */}
          <Box
            sx={{
              flex: isMobile ? "none" : "0 1 140px",
              minWidth: isMobile ? "100%" : 120,
              maxWidth: isMobile ? "100%" : 180,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              flexShrink: 0,
              py: 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
              <InventoryIcon
                sx={{ color: SUBTITLE_COLOR, fontSize: 13, mr: 0.7 }}
              />
              <Typography
                variant="body2"
                color={SUBTITLE_COLOR}
                fontWeight={600}
                sx={{ fontSize: isMobile ? "0.80rem" : "0.90rem" }}
              >
                Resources Scanned: {total}
              </Typography>
            </Box>
            <Tooltip
              title={`Passed: ${passed} | Failed: ${failed}`}
              arrow
              placement="top"
            >
              <Box
                sx={{
                  position: "relative",
                  height: 7,
                  width: "100%",
                  mb: 0.5,
                }}
              >
                {/* Single progress bar with two colors */}
                <Box
                  sx={{
                    position: "relative",
                    height: "100%",
                    width: "100%",
                    borderRadius: 5,
                    overflow: "hidden",
                    display: "flex",
                    bgcolor: FAILED_COLOR,
                  }}
                >
                  <Box
                    sx={{
                      width: `${passedPercent}%`,
                      height: "100%",
                      bgcolor: PASSED_COLOR,
                    }}
                  />
                </Box>
              </Box>
            </Tooltip>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{
                  color: PASSED_TEXT_COLOR,
                  fontWeight: 600,
                  fontSize: "0.68rem",
                }}
              >
                {passed} Passed
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: FAILED_TEXT_COLOR,
                  fontWeight: 600,
                  fontSize: "0.68rem",
                }}
              >
                {failed} Failed
              </Typography>
            </Box>
          </Box>

          {/* Divider - hidden on small screens */}
          {!isMobile && (
            <Box
              sx={{
                width: "1px",
                background: DIVIDER_COLOR,
                mx: 0.5,
                my: 1,
                alignSelf: "stretch",
                flexShrink: 0,
              }}
            />
          )}

          {/* Trend Section - Responsive width, fills available space */}
          <Box
            sx={{
              flex: isMobile ? "none" : "2 1 0",
              minWidth: isMobile ? "100%" : 180,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden", // Prevent overflow
              py: 0.5,
            }}
          >
            <Box
              sx={{
                width: "100%",
                px: 1,
                py: 0.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="subtitle2"
                color={TITLE_COLOR}
                fontWeight={700}
                sx={{
                  fontSize: isMobile ? 12 : 13,
                  mb: 0.2,
                  textAlign: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
              >
                Compliance Trend (Last 30 Days)
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  height: 56,
                  overflow: "visible",
                  transition: "box-shadow 0.2s",
                  borderRadius: 2,
                  "&:hover": {
                    boxShadow: "0 4px 16px 0 rgba(25, 118, 210, 0.10)",
                  },
                  position: 'relative',
                }}
              >
                <Line ref={chartRef} data={chartData} options={chartOptions} />
                {/* External Tooltip */}
                {tooltip.display && (
                  <Box
                    sx={{
                      position: 'fixed',
                      left: tooltip.x,
                      top: tooltip.y,
                      zIndex: 1500,
                      pointerEvents: 'none',
                      bgcolor: '#fff',
                      color: '#1a2233',
                      border: '1px solid #e3eafc',
                      borderRadius: 2.5,
                      boxShadow: '0 6px 24px rgba(25, 118, 210, 0.13)',
                      p: 2.2,
                      minWidth: 170,
                      fontSize: 15,
                      fontWeight: 500,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 1.2,
                    }}
                  >
                    {/* Date row as header */}
                    <Typography variant="subtitle2" sx={{ fontSize: 15, color: '#1a2233', fontWeight: 700, mb: 0.5, width: '100%' }}>
                      {tooltip.date}
                    </Typography>
                    {/* Compliance Score row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: TREND_COLOR, mr: 1 }} />
                      <Typography variant="body2" sx={{ fontSize: 14, color: '#888', fontWeight: 500 }}>
                        Compliance Score:
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: 15, color: TREND_COLOR, fontWeight: 700, ml: 1 }}>
                        {Number(tooltip.value).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
              {/* Delta info below chart - Hide on small screens */}
              {trendChange && trendChange.direction !== "none" && !isMobile && (
                <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                  {trendChange.direction === "up" ? (
                    <ArrowUpwardIcon
                      sx={{ color: "#4caf50", fontSize: 13, mr: 0.5 }}
                    />
                  ) : (
                    <ArrowDownwardIcon
                      sx={{ color: "#e57373", fontSize: 13, mr: 0.5 }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        trendChange.direction === "up" ? "#4caf50" : "#e57373",
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  >
                    {Number(trendChange.percent).toFixed(2)}%{" "}
                    {trendChange.direction === "up" ? "higher" : "lower"} than
                    last scan
                  </Typography>
                </Box>
              )}
              {trendChange && trendChange.direction === "none" && !isMobile && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#888",
                    mt: 0.5,
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  No change since last scan
                </Typography>
              )}
            </Box>
          </Box>

          {/* Divider - hidden on small screens */}
          {!isMobile && (
            <Box
              sx={{
                width: "1px",
                background: DIVIDER_COLOR,
                mx: 0.5,
                my: 1,
                alignSelf: "stretch",
                flexShrink: 0,
              }}
            />
          )}

          {/* Back + Download — stacked vertically (Back above download) */}
          <Box
            sx={{
              flex: isMobile ? "none" : "0 0 auto",
              alignSelf: "stretch",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              flexShrink: 0,
              width: isMobile ? "100%" : "auto",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                borderColor: "#1976d2",
                color: "#1976d2",
                "&:hover": {
                  borderColor: "#1565c0",
                  backgroundColor: "#e3f2fd",
                },
              }}
            >
              Back
            </Button>
            <Tooltip title="Download Compliance Report">
              <span>
                <IconButton
                  onClick={handleDownloadReport}
                  disabled={isDownloadingComplianceReport}
                  sx={{
                    color: "#1976d2",
                    fontSize: isMobile ? 24 : isLargeScreen ? 30 : 28,
                    p: isMobile ? 1 : isLargeScreen ? 1.2 : 1,
                    border: "2px solid #1976d2",
                    background: "rgba(25, 118, 210, 0.08)",
                    "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.18)" },
                    boxShadow: "0 2px 8px 0 rgba(25, 118, 210, 0.10)",
                  }}
                >
                  {isDownloadingComplianceReport ? (
                    <CircularProgress
                      size={isMobile ? 20 : isLargeScreen ? 24 : 22}
                    />
                  ) : (
                    <DownloadIcon
                      sx={{ fontSize: isMobile ? 24 : isLargeScreen ? 28 : 26 }}
                    />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ControlsHeader;
